import React, { useState, useEffect } from 'react';
import { Input, Button, Modal, notification, Typography, ConfigProvider } from 'antd';
import { KeyOutlined } from '@ant-design/icons';
import Cookies from 'js-cookie';
import firefliesIcon from '../../icons/fireflies-icon.webp';

interface FirefliesAuthProps {
  onTokenReceived?: (token: string) => void;
  onUserNameReceived?: (username: string) => void;
  disabled: boolean;
}

const { Title, Text } = Typography;

// Design system constants matching Settings page
const COLORS = {
  background: 'var(--primary-bg-color)',
  cardBg: 'var(--card-bg-dark)',
  border: '#444',
  textPrimary: 'var(--text-color)',
  textSecondary: '#ccc',
  textMuted: 'var(--text-light)',
  buttonPrimary: 'var(--primary-color)',
  inputBg: 'transparent',
  contentBg: '#3a3a3a',
};

const SIZES = {
  borderRadius: '12px',
  spacing: {
    xs: '8px',
    sm: '12px',
    md: '16px',
    lg: '24px',
    xl: '32px',
  },
};

const FirefliesAuth: React.FC<FirefliesAuthProps> = ({ disabled = false, onTokenReceived }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);

  const handleTokenSubmit = () => {
    if (!token.trim()) {
      notification.error({
        message: 'Error',
        description: 'Please enter a valid Fireflies API token',
      });
      return;
    }

    setLoading(true);

    // Save token to cookie
    Cookies.set('fireflies_access_token', token.trim(), { path: '/', expires: 3 }); // Expires in 3 days
    
    // Call callback if provided
    if (onTokenReceived) {
      onTokenReceived(token.trim());
    }

    setLoading(false);
    setModalVisible(false);
    setToken('');

    notification.success({
      message: 'Fireflies Connected',
      description: 'Your Fireflies API token has been saved successfully.',
    });
  };

  return (
    <div style={{ width: '100%', overflow: 'hidden', display: 'flex', justifyContent: 'flex-end' }}>
      <Button
        onClick={() => setModalVisible(true)}
        disabled={disabled}
        style={{
          backgroundColor: disabled ? '#6b7280' : '#3A425E',
          color: 'white',
          border: 'none',
          fontFamily: 'Inter, sans-serif',
          fontSize: '13px',
          fontWeight: 'bold',
          display: 'inline-flex',
          alignItems: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          padding: '6px 12px',
          height: 'auto',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          width: '100%',
          maxWidth: '200px',
          minWidth: '0',
          boxSizing: 'border-box',
          justifyContent: 'center',
        }}
        icon={
          <img 
            src={firefliesIcon} 
            alt="Fireflies" 
            style={{ 
              width: '16px', 
              height: '16px',
              marginRight: '4px',
              flexShrink: 0,
            }} 
          />
        }
      >
        <span style={{ 
          overflow: 'hidden', 
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
          flex: 1,

        }}>
          Connect Fireflies
        </span>
      </Button>

      <Modal
        title={null}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width="90vw"
        style={{ maxWidth: '500px' }}
        styles={{
          content: {
            background: COLORS.cardBg,
            border: `1px solid ${COLORS.border}`,
          },
          header: {
            background: COLORS.cardBg,
            borderBottom: `1px solid ${COLORS.border}`,
          }
        }}
      >
        <div>
          {/* Header */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: SIZES.spacing.sm,
            marginBottom: SIZES.spacing.lg,
            paddingBottom: SIZES.spacing.md,
            borderBottom: `1px solid ${COLORS.border}`
          }}>
            <img 
              src={firefliesIcon} 
              alt="Fireflies" 
              style={{ 
                width: '24px', 
                height: '24px'
              }} 
            />
            <Title level={4} style={{ margin: 0, color: COLORS.textPrimary }}>
              Connect Fireflies.ai
            </Title>
          </div>

          {/* Description */}
          <div style={{ 
            marginBottom: SIZES.spacing.lg,
            padding: SIZES.spacing.md,
            background: COLORS.contentBg,
            borderRadius: SIZES.spacing.xs,
            border: `1px solid ${COLORS.border}`
          }}>
            <Text style={{ color: COLORS.textPrimary, display: 'block', marginBottom: SIZES.spacing.xs }}>
              Enter your Fireflies.ai API token to connect your account
            </Text>
            <Text style={{ color: COLORS.textMuted, fontSize: '12px' }}>
              You can find your API token in your Fireflies.ai account settings.
            </Text>
          </div>

          {/* Token Input */}
          <div style={{ marginBottom: SIZES.spacing.lg }}>
            <Text style={{ color: COLORS.textSecondary, display: 'block', marginBottom: SIZES.spacing.sm }}>
              API Token
            </Text>
            <ConfigProvider
              theme={{
                components: {
                  Input: {
                    colorText: COLORS.textPrimary,
                    colorBorder: COLORS.border,
                    colorBgContainer: COLORS.inputBg,
                    colorTextDisabled: COLORS.textMuted,
                    colorTextPlaceholder: COLORS.textMuted,
                  },
                },
              }}
            >
              <Input.Password
                placeholder="Enter your Fireflies API token"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                prefix={<KeyOutlined style={{ color: COLORS.textMuted }} />}
                onPressEnter={handleTokenSubmit}
                size="large"
              />
            </ConfigProvider>
          </div>

          {/* Action Buttons */}
          <div style={{ 
            display: 'flex', 
            gap: SIZES.spacing.sm, 
            justifyContent: 'flex-end',
            paddingTop: SIZES.spacing.md,
            borderTop: `1px solid ${COLORS.border}`
          }}>
            <Button 
              onClick={() => setModalVisible(false)}
              style={{
                background: 'transparent',
                borderColor: COLORS.border,
                color: COLORS.textSecondary,
              }}
            >
              Cancel
            </Button>
            <Button 
              type="primary" 
              loading={loading}
              onClick={handleTokenSubmit}
              style={{
                background: COLORS.buttonPrimary,
                borderColor: COLORS.buttonPrimary,
                color: COLORS.background,
                fontWeight: 'bold',
              }}
            >
              Connect
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default FirefliesAuth;
